import { LoadingButton } from "@mui/lab";
import {
  Box,
  Button,
  Container,
  FormControl,
  FormHelperText,
  Grid,
  InputAdornment,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { DatePicker, DateTimePicker } from "@mui/x-date-pickers";
import { enUS } from "@mui/x-date-pickers/locales";
import { useMediaQuery } from "@react-hook/media-query";
import { Moment } from "moment";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import useContactValidation, { IProps } from "../../components/Validation/contact";
import useDietitians from "../../hooks/useDietitians";
import usePartner from "../../hooks/usePartner";
import {
  DietitianAppointmentAnonymousRequest,
  DietitianAppointmentAnonymousRequestGenderEnum,
  DietitianAppointmentMemberRequest,
} from "../../openapi/client";
import theme, { getTenant } from "../../theme";
import {
  arabicLocaleText,
  checkEmpty,
  checkEmptyObj,
  checkIf,
  defaultFormat,
  displayFormat,
  displayFormatForDateTime,
  formatDateForDisplay,
  siteKey,
} from "../../utils";
import Api from "../../openapi";
import { setNotification } from "../../store/notificationSlice";
import useDietaryGoals from "../../hooks/useDietaryGoals";
import { CalendarBigIcon, QatarIcon } from "../../assets/icons";
import usePartners from "../../hooks/usePartners";
import { RootState } from "../../store";
import moment from "moment";
import { SuccessLabelIcon } from "../../components/IconLabel";
import ReCAPTCHA from "react-google-recaptcha";
import useRecaptcha from "../../hooks/useRecaptcha";
import { useTranslation } from "react-i18next";
import "moment/locale/ar";

export default function DietitianAppointment(props: { showTitle?: boolean }) {
  const { t, i18n } = useTranslation("dietitianAppointment");
  const mobile = useMediaQuery("only screen and (max-width: 600px)");
  const user = useSelector((state: RootState) => state.user);
  const { capchaToken, recaptchaRef, handleRecaptcha } = useRecaptcha();
  let [searchParams] = useSearchParams();
  const result = searchParams.get("r");
  const partners = usePartners();
  const navigate = useNavigate();
  const isArabic = i18n.language === "ar";
  const tenant = getTenant();

  // Helper function to convert API keys to translation keys
  const getTranslationKey = (apiKey: string): string => {
    // Convert WEIGHT_LOSS to weightLoss, etc.
    return apiKey
      .split("_")
      .map((word, index) =>
        index === 0 ? word.toLowerCase() : word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
      )
      .join("");
  };

  const defaultInfo: IProps = {
    email: "",
    dietaryGoals: [],
    additionalNotes: "",
    partnerId: undefined,
    fullName: user.fullName || "",
    dateOfBirth: user.birthDate ? moment(user.birthDate, defaultFormat, "en").locale(isArabic ? "ar" : "en") : null,
    gender: user.gender || undefined,
    contactNumber: user.phoneNumber || "",
  };
  const [appointmentInfo, setAppointmentInfo] = useState<IProps>(defaultInfo);
  // const dietitians = useDietitians(appointmentInfo.partnerId);
  const dietaryGoals = useDietaryGoals();
  const [open, setOpen] = useState(false);
  const [open2, setOpen2] = useState(false);
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();

  const { validation, setValidation } = useContactValidation(appointmentInfo as IProps);

  // Set moment locale based on current language for calendar display
  useEffect(() => {
    moment.locale(isArabic ? "ar" : "en");
  }, [isArabic]);

  const handleFieldChange = (field: any, value?: any) => {
    let fieldValue = value;
    if (field === "dietaryGoals") {
      setAppointmentInfo((state: any) => ({ ...state, dietaryGoals: [fieldValue] }));
    } else if (field === "dateOfBirth" && value) {
      // Set date with current UI locale for display
      const dateWithLocale = moment(value).locale(isArabic ? "ar" : "en");
      setAppointmentInfo((state: any) => ({ ...state, [field]: dateWithLocale }));
    } else {
      setAppointmentInfo((state: any) => ({ ...state, [field]: fieldValue }));
    }
  };

  // console.log("captcha token", capchaToken);
  // console.log("validation", validation);
  // console.log("appointment info", appointmentInfo);

  const sendRequest = async () => {
    if (validation.error) {
      setValidation((state) => ({ ...state, show: true }));
      return;
    }
    if (!capchaToken) return;

    try {
      setLoading(true);
      const { dateOfBirth, gender, fullName, contactNumber, ...rest } = appointmentInfo as IProps;

      if (checkEmptyObj(user)) {
        const transmitData: DietitianAppointmentAnonymousRequest = {
          ...rest,
          captchaToken: capchaToken,
          dateOfBirth: String(dateOfBirth?.locale("en").format(defaultFormat)),
          partnerId: String(rest.partnerId),
          fullName: String(fullName),
          gender: gender as DietitianAppointmentAnonymousRequestGenderEnum,
          contactNumber: String(contactNumber),
        };
        await Api.Dietitian.createAnonymousDietitianAppointment(transmitData);
      } else {
        const transmitData: DietitianAppointmentMemberRequest = {
          ...rest,
          captchaToken: capchaToken,
          partnerId: String(rest.partnerId),
        };
        await Api.Dietitian.createMemberDietitianAppointment(transmitData);
      }
      setValidation((state) => ({ ...state, show: false }));

      if (props.showTitle === false) {
        dispatch(
          setNotification({
            show: true,
            severity: "success",
            message: t("appointmentSubmitted"),
          })
        );
        setAppointmentInfo((state) => ({
          email: "",
          dietaryGoals: [],
          additionalNotes: "",
          partnerId: state?.partnerId || undefined,
        }));
      } else {
        navigate("/dietitian-appointment?r=success");
      }
    } catch (error: any) {
      dispatch(
        setNotification({
          show: true,
          severity: "error",
          message: error.response?.data?.message || error.message,
        })
      );
    }
    handleRecaptcha("");
    if (recaptchaRef.current) {
      recaptchaRef.current.reset();
    }
    setLoading(false);
  };

  useEffect(() => {
    if (partners) {
      setAppointmentInfo((state) => ({ ...state, partnerId: partners[0].id }));
    }
  }, [partners]);
  useEffect(() => {
    if (checkEmptyObj(user)) {
      setAppointmentInfo((state) => ({
        email: "",
        dietaryGoals: [],
        additionalNotes: "",
        partnerId: state?.partnerId || undefined,
        fullName: "",
        dateOfBirth: null,
        gender: null,
        contactNumber: "",
      }));
    } else {
      setAppointmentInfo((state) => ({
        email: "",
        dietaryGoals: [],
        additionalNotes: "",
        partnerId: state?.partnerId || undefined,
      }));
    }
  }, [user]);

  // console.log("appointment info", appointmentInfo);
  // console.log("validation", validation);

  return (
    <>
      {result === "success" ? (
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "20px", paddingTop: "20px" }}>
          <SuccessLabelIcon />
          <Typography
            sx={{
              color: "text.primaryDefault",
              fontSize: "18px",
              fontWeight: 700,
              lineHeight: "normal",
            }}
          >
            {t("congratulations")}
          </Typography>
          <Typography
            sx={{
              color: "text.base900",
              fontSize: "16px",
              fontWeight: 500,
              lineHeight: "22px",
              textAlign: "center",
            }}
          >
            {t("appointmentSubmitted")}
          </Typography>
          <Link to={tenant.name === "rejeem" ? `/all-plans` : `/plans`}>
            <Button sx={{ width: ["100%", "auto"] }} color="primary" variant="contained" disableElevation>
              {t("explorePlans")}
            </Button>
          </Link>
        </Box>
      ) : (
        <Box
          sx={{
            padding: "16px 16px 16px 16px",
            display: "flex",
            flexDirection: "column",
            gap: "64px",
            alignItems: "center",
            justifyContent: "center",
            "& .text_input:": { width: "100%", fontSize: "14px", fontWeight: 500, lineHeight: "22px" },
            "& .profile_field": {
              display: "flex",
              flexDirection: "column",
              gap: "8px",
              flex: 1,
            },
            "& .profile_field > .MuiBox-root": { gap: "4px" },
            "& .profile_field > .MuiBox-root .required-label": { lineHeight: "normal" },
            "& .MuiOutlinedInput-root": { margin: "0" },
            "& .MuiFormHelperText-root": {
              marginTop: "0",
              textAlign: i18n.language === "ar" ? "right" : "left",
            },
          }}
        >
          {props.showTitle === false ? null : (
            <Typography
              sx={{
                maxWidth: "728px",
                color: "#191E2A",
                fontSize: ["18px", "24px"],
                fontWeight: 700,
                lineHeight: "150%",
                letterSpacing: "-0.48px",
                textAlign: "center",
                display: ["none", "flex"],
                paddingTop: "48px",
              }}
            >
              {t("title")}
            </Typography>
          )}
          <Box
            component="form"
            noValidate
            sx={{ width: ["100%", "420px"], display: "flex", flexDirection: "column", gap: "16px" }}
          >
            {checkEmptyObj(user) ? (
              <Box className={"profile_field"}>
                <Box sx={{ display: "flex" }}>
                  <Typography component="span" sx={{ fontSize: "14px", fontWeight: 600, lineHeight: "normal" }}>
                    {t("fullName")}
                  </Typography>
                  <Typography component="span" className="required-label">
                    *
                  </Typography>
                </Box>
                <TextField
                  placeholder={t("enterName")}
                  className={"text_input"}
                  value={appointmentInfo?.fullName || ""}
                  onChange={(e) => handleFieldChange("fullName", e.target.value)}
                  error={validation.show && validation.fullName?.error}
                  helperText={
                    validation.show && validation.fullName?.error ? validation.fullName.helperText : undefined
                  }
                />
              </Box>
            ) : null}

            {checkEmptyObj(user) ? (
              <Box className={"profile_field"}>
                <Box sx={{ display: "flex" }}>
                  <Typography component="span" sx={{ fontSize: "14px", fontWeight: 600, lineHeight: "normal" }}>
                    {t("dateOfBirth")}
                  </Typography>
                  <Typography component="span" className="required-label">
                    *
                  </Typography>
                </Box>

                <DatePicker
                  sx={{
                    "& .MuiFormHelperText-root": {
                      bottom: "-20px",
                      left: 0,
                      right: 0,
                      color: "text.errorDefault",
                    },
                    "&.Mui2-error .MuiOutlinedInput-notchedOutline": { borderColor: "#ff4040 !important" },
                    "&.Mui2-error .MuiFormHelperText-root": { color: "#ff4040 !important" },
                  }}
                  localeText={
                    isArabic ? arabicLocaleText : enUS.components.MuiLocalizationProvider.defaultProps.localeText
                  }
                  value={appointmentInfo?.dateOfBirth}
                  open={open}
                  className={`${validation.show && validation.dateOfBirth?.error ? "Mui2-error" : ""}`}
                  format={displayFormat}
                  referenceDate={moment().add(-15, "years")}
                  onChange={(newValue) => handleFieldChange("dateOfBirth", newValue as Moment)}
                  onAccept={() => setOpen(false)}
                  onClose={() => setOpen(false)}
                  slotProps={{
                    textField: {
                      onClick: () => setOpen(true),
                      placeholder: isArabic ? "يوم/شهر/سنة" : "dd/mm/yyyy",
                      inputProps: {
                        value: formatDateForDisplay(appointmentInfo?.dateOfBirth || null, isArabic),
                        readOnly: true,
                      },
                      helperText:
                        validation.show && validation.dateOfBirth?.error ? validation.dateOfBirth.helperText : null,
                    },
                  }}
                />
              </Box>
            ) : null}

            {checkEmptyObj(user) ? (
              <Box className={"profile_field"}>
                <Box sx={{ display: "flex" }}>
                  <Typography component="span" sx={{ fontSize: "14px", fontWeight: 600, lineHeight: "normal" }}>
                    {t("gender")}
                  </Typography>
                  <Typography component="span" className="required-label">
                    *
                  </Typography>
                </Box>
                <FormControl fullWidth error={validation.show && validation.gender?.error}>
                  <Select
                    value={appointmentInfo?.gender || " "}
                    onChange={(e) => handleFieldChange("gender", e.target.value)}
                    sx={{
                      "& .MuiSelect-icon": {
                        ...(i18n.language === "ar" && { left: "7px", right: "auto" }),
                      },
                    }}
                  >
                    {/* <MenuItem value=" ">Choose</MenuItem> */}
                    <MenuItem value={DietitianAppointmentAnonymousRequestGenderEnum.Male}>{t("male")}</MenuItem>
                    <MenuItem value={DietitianAppointmentAnonymousRequestGenderEnum.Female}>{t("female")}</MenuItem>
                  </Select>
                  {validation.show && validation.gender?.error ? (
                    <FormHelperText>{validation.gender.helperText}</FormHelperText>
                  ) : null}
                </FormControl>
              </Box>
            ) : null}

            {checkEmptyObj(user) ? (
              <Box className={"profile_field"}>
                <Box sx={{ display: "flex" }}>
                  <Typography component="span" sx={{ fontSize: "14px", fontWeight: 600, lineHeight: "normal" }}>
                    {t("contactNumber")}
                  </Typography>
                  <Typography component="span" className="required-label">
                    *
                  </Typography>
                </Box>

                <TextField
                  value={appointmentInfo?.contactNumber?.slice(4) || ""}
                  onChange={(e) => handleFieldChange("contactNumber", `+974${e.target.value}`)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start" sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        <QatarIcon />
                        <Typography>+974</Typography>
                      </InputAdornment>
                    ),
                  }}
                  inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                  error={validation.show && validation.contactNumber?.error}
                  helperText={
                    validation.show && validation.contactNumber?.error ? validation.contactNumber.helperText : undefined
                  }
                />
              </Box>
            ) : null}

            <Box className={"profile_field"}>
              <Box sx={{ display: "flex" }}>
                <Typography component="span" sx={{ fontSize: "14px", fontWeight: 600, lineHeight: "normal" }}>
                  {t("email")}
                </Typography>
                <Typography component="span" className="required-label">
                  *
                </Typography>
              </Box>
              <TextField
                placeholder={t("enterEmail")}
                className={"text_input"}
                value={appointmentInfo?.email || ""}
                onChange={(e) => handleFieldChange("email", e.target.value)}
                error={validation.show && validation.email?.error}
                helperText={validation.show && validation.email?.error ? validation.email.helperText : undefined}
              />
            </Box>

            <Box className={"profile_field"}>
              <Box sx={{ display: "flex" }}>
                <Typography component="span" sx={{ fontSize: "14px", fontWeight: 600, lineHeight: "normal" }}>
                  {t("dietaryGoals")}
                </Typography>
                <Typography component="span" className="required-label">
                  *
                </Typography>
              </Box>
              <FormControl fullWidth error={validation.show && validation.dietaryGoals?.error}>
                <Select
                  value={appointmentInfo?.dietaryGoals?.[0] || ""}
                  onChange={(e) => handleFieldChange("dietaryGoals", e.target.value)}
                  sx={{
                    "& .MuiSelect-icon": {
                      ...(i18n.language === "ar" && { left: "7px", right: "auto" }),
                    },
                  }}
                >
                  {/* <MenuItem value=" ">Choose</MenuItem> */}
                  {dietaryGoals
                    ? Object.keys(dietaryGoals).map((apiKey) => (
                        <MenuItem key={apiKey} value={apiKey}>
                          {t(getTranslationKey(apiKey))}
                        </MenuItem>
                      ))
                    : null}
                </Select>
                {validation.show && validation.dietaryGoals?.error ? (
                  <FormHelperText>{validation.dietaryGoals.helperText}</FormHelperText>
                ) : null}
              </FormControl>
            </Box>

            {/* FOLLOWING LINE REMOVED - NOT TO INCLUDE DATE */}
            {/* <Box className={"profile_field"}>
              <Box sx={{ display: "flex" }}>
                <Typography component="span" sx={{ fontSize: "14px", fontWeight: 600, lineHeight: "normal" }}>
                  Appointment Date
                </Typography>
                <Typography component="span" className="required-label">
                  *
                </Typography>
              </Box>

              <DatePicker
                sx={{
                  "& .MuiFormHelperText-root": {
                    bottom: "-20px",
                    left: 0,
                    right: 0,
                    color: "text.errorDefault",
                  },
                  "&.Mui2-error .MuiOutlinedInput-notchedOutline": { borderColor: "#ff4040 !important" },
                  "&.Mui2-error .MuiFormHelperText-root": { color: "#ff4040 !important" },
                }}
                className={`${validation.show && validation.dateTime?.error ? "Mui2-error" : ""}`}
                value={appointmentInfo?.dateTime}
                open={open2}
                closeOnSelect={true}
                format={displayFormat}
                disablePast
                onChange={(newValue) => handleFieldChange("dateTime", newValue as Moment)}
                onAccept={() => setOpen2(false)}
                onClose={() => setOpen2(false)}
                slotProps={{
                  textField: {
                    onClick: () => setOpen2(true),
                    helperText: validation.show && validation.dateTime?.error ? validation.dateTime.helperText : null,
                  },
                }}
              />
            </Box> */}

            <Box className={"profile_field"}>
              <Box sx={{ display: "flex" }}>
                <Typography component="span" sx={{ fontSize: "14px", fontWeight: 600, lineHeight: "normal" }}>
                  {t("additionalNotes")}
                </Typography>
              </Box>
              <TextField
                sx={{ "& .MuiInputBase-root": { height: "auto" } }}
                className={"text_input"}
                placeholder={t("addNotes")}
                value={appointmentInfo?.additionalNotes || ""}
                onChange={(e) => handleFieldChange("additionalNotes", e.target.value)}
                multiline
              />
            </Box>

            <ReCAPTCHA ref={recaptchaRef} sitekey={String(siteKey)} size="normal" onChange={handleRecaptcha} />

            <LoadingButton
              fullWidth
              color="primary"
              disabled={!capchaToken}
              loading={loading}
              variant="contained"
              disableElevation
              onClick={sendRequest}
            >
              {loading ? <span>&nbsp;</span> : <span>{t("send")}</span>}
            </LoadingButton>
          </Box>
        </Box>
      )}
    </>
  );
}
